import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

type ViewState = 'loading' | 'password' | 'viewer' | 'expired' | 'not_found' | 'error';

export default function SharedViewerPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [viewState, setViewState] = useState<ViewState>('loading');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isCssFullscreen, setIsCssFullscreen] = useState(false);
  const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });
  const currentPageRef = useRef(1);

  const callEdgeFunction = async (pwd?: string) => {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/shared-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ token, password: pwd || null }),
    });
    return res.json();
  };

  // 초기 로드
  useEffect(() => {
    if (!token) {
      setViewState('not_found');
      return;
    }

    const init = async () => {
      try {
        const data = await callEdgeFunction();
        if (data.success) {
          setPdfUrl(data.signedUrl);
          setFileName(data.fileName);
          setPageCount(data.pageCount);
          setViewState('viewer');
        } else if (data.error === 'PASSWORD_REQUIRED') {
          setViewState('password');
        } else if (data.error === 'EXPIRED') {
          setViewState('expired');
        } else if (data.error === 'NOT_FOUND') {
          setViewState('not_found');
        } else {
          setViewState('error');
        }
      } catch {
        setViewState('error');
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // 비밀번호 제출
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;

    setSubmitting(true);
    setPasswordError(false);

    try {
      const data = await callEdgeFunction(password);
      if (data.success) {
        setPdfUrl(data.signedUrl);
        setFileName(data.fileName);
        setPageCount(data.pageCount);
        setViewState('viewer');
      } else if (data.error === 'WRONG_PASSWORD') {
        setPasswordError(true);
      } else if (data.error === 'EXPIRED') {
        setViewState('expired');
      } else {
        setViewState('error');
      }
    } catch {
      setViewState('error');
    } finally {
      setSubmitting(false);
    }
  };

  // 페이지 이동
  const goToPage = (page: number) => {
    const newPage = Math.max(1, Math.min(page, pageCount));
    setPageNumber(newPage);
    currentPageRef.current = newPage;
  };

  // 키보드 이벤트
  useEffect(() => {
    if (viewState !== 'viewer' || pageCount === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        goToPage(currentPageRef.current + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        goToPage(currentPageRef.current - 1);
      } else if (e.key === 'Escape' && isCssFullscreen) {
        exitFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState, pageCount, isCssFullscreen]);

  // 리사이즈
  useEffect(() => {
    const handleResize = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 전체화면
  const enterFullscreen = async () => {
    try {
      await document.documentElement.requestFullscreen();
    } catch { /* CSS 전체화면으로 대체 */ }
    setIsCssFullscreen(true);
    document.body.style.overflow = 'hidden';
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    setIsCssFullscreen(false);
    document.body.style.overflow = '';
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isCssFullscreen) {
        setIsCssFullscreen(false);
        document.body.style.overflow = '';
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [isCssFullscreen]);

  // 로딩
  if (viewState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  // 비밀번호 입력
  if (viewState === 'password') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-line text-2xl text-amber-500"></i>
            </div>
            <h1 className="text-xl font-bold text-gray-900">{t('shared.password_required')}</h1>
            <p className="text-sm text-gray-500 mt-1">{t('shared.password_required_desc')}</p>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(false); }}
                placeholder={t('shared.enter_password')}
                autoFocus
                className={`w-full px-4 py-3 bg-gray-50 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  passwordError ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
              />
              {passwordError && (
                <p className="mt-2 text-xs text-red-500">{t('shared.wrong_password')}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting || !password.trim()}
              className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm font-medium transition-all cursor-pointer disabled:opacity-50"
            >
              {submitting ? t('common.confirming') : t('common.confirm')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // 만료됨
  if (viewState === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-time-line text-3xl text-gray-400"></i>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('shared.link_expired')}</h1>
          <p className="text-sm text-gray-500">{t('shared.link_expired_desc')}</p>
        </div>
      </div>
    );
  }

  // 404
  if (viewState === 'not_found') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-unknow-line text-3xl text-gray-400"></i>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('shared.not_found')}</h1>
          <p className="text-sm text-gray-500">{t('shared.not_found_desc')}</p>
        </div>
      </div>
    );
  }

  // 에러
  if (viewState === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-3xl text-red-400"></i>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('shared.error')}</h1>
          <p className="text-sm text-gray-500">{t('shared.error_desc')}</p>
        </div>
      </div>
    );
  }

  // 전체화면 뷰어
  if (isCssFullscreen && pdfUrl) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
        <Document
          file={pdfUrl}
          loading={
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
            </div>
          }
        >
          <Page
            pageNumber={pageNumber}
            width={dimensions.width}
            height={dimensions.height}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            canvasBackground="black"
          />
        </Document>

        {/* 페이지 네비게이션 */}
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10002] flex items-center gap-3 bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 opacity-0 hover:opacity-100 transition-opacity">
          <button
            onClick={() => goToPage(pageNumber - 1)}
            disabled={pageNumber <= 1}
            className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-all cursor-pointer disabled:opacity-30"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <span className="text-white text-sm font-medium min-w-[60px] text-center">
            {pageNumber} / {pageCount}
          </span>
          <button
            onClick={() => goToPage(pageNumber + 1)}
            disabled={pageNumber >= pageCount}
            className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-all cursor-pointer disabled:opacity-30"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>

        {/* 전체화면 종료 */}
        <button
          onClick={exitFullscreen}
          className="fixed top-4 right-4 z-[10002] w-10 h-10 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-all cursor-pointer backdrop-blur-sm opacity-0 hover:opacity-100"
        >
          <i className="ri-fullscreen-exit-line text-xl"></i>
        </button>
      </div>
    );
  }

  // 일반 뷰어
  const BAR_HEIGHT = 48;

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* 상단 바 */}
      <div
        className="flex items-center justify-between px-4 bg-black/70 backdrop-blur-sm flex-shrink-0"
        style={{ height: BAR_HEIGHT }}
      >
        <span className="text-white/80 text-sm truncate max-w-xs font-medium">
          {fileName?.replace('.pdf', '') || t('common.presentation')}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-white/50 text-xs">
            {pageNumber} / {pageCount}
          </span>
          <button
            onClick={enterFullscreen}
            className="flex items-center gap-1.5 px-3 py-1.5 hover:bg-white/10 text-white rounded-md text-xs font-medium transition-all cursor-pointer"
          >
            <i className="ri-fullscreen-line"></i>
            {t('common.fullscreen')}
          </button>
        </div>
      </div>

      {/* PDF */}
      <div className="flex-1 flex items-center justify-center overflow-hidden relative">
        {pdfUrl ? (
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages }) => setPageCount(numPages)}
            loading={
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              width={dimensions.width}
              height={dimensions.height - BAR_HEIGHT}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              canvasBackground="black"
            />
          </Document>
        ) : (
          <div className="text-center text-gray-400">
            <i className="ri-file-pdf-line text-5xl mb-3 block"></i>
            <p>{t('shared.loading_pdf')}</p>
          </div>
        )}

        {/* 이전 페이지 */}
        {pageNumber > 1 && (
          <button
            onClick={() => goToPage(pageNumber - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-all cursor-pointer backdrop-blur-sm opacity-0 hover:opacity-100"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
        )}

        {/* 다음 페이지 */}
        {pageNumber < pageCount && (
          <button
            onClick={() => goToPage(pageNumber + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-all cursor-pointer backdrop-blur-sm opacity-0 hover:opacity-100"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        )}
      </div>
    </div>
  );
}
